import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Shield, Trash2, User } from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Team() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setRows(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 8) {
      toast.error("Fill in every field, with at least 8 characters for the password");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.name} can now sign in`);
      setAdding(false);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (row, role) => {
    try {
      await api.patch(`/admin/users/${row.user_id}/role`, { role });
      toast.success(`${row.name} is now ${role === "admin" ? "an admin" : "staff"}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Until email is configured this is how someone locked out gets back in:
  // you generate the link and pass it to them.
  const makeResetLink = async (row) => {
    try {
      const { data } = await api.post(`/admin/users/${row.user_id}/reset-link`);
      try {
        await navigator.clipboard.writeText(data.link);
        toast.success(`Reset link copied — send it to ${row.name}. Valid ${data.expires_in_minutes} minutes.`);
      } catch {
        window.prompt(`Copy this link and send it to ${row.name}:`, data.link);
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Remove ${row.name}?`,
      body: "They'll be signed out immediately and won't be able to sign back in.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${row.user_id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Team"
      subtitle="Logins for this client. Admins change settings and delete items; staff log purchases, sales and boxes."
      action={
        <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add person</span>
        </Button>
      }
    >
      {loading ? (
        <LoadingPanel rows={4} />
      ) : (
        <div className="panel overflow-hidden max-w-4xl">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px]">
              <thead>
                <tr className="border-b border-line bg-surface/60">
                  {["Name", "Email", "Role", "Joined", ""].map((h, i) => (
                    <th key={h + i} className="t-head px-4 py-3 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSelf = r.user_id === user?.user_id;
                  return (
                    <tr key={r.user_id} className="t-row last:border-0 group">
                      <td className="t-cell">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[12px] shrink-0">
                            {(r.name?.[0] || "U").toUpperCase()}
                          </div>
                          <span className="font-medium">
                            {r.name}
                            {isSelf && <span className="text-muted2 font-normal"> (you)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="t-cell text-muted2">{r.email}</td>
                      <td className="t-cell">
                        <Select
                          value={r.role}
                          onValueChange={(v) => changeRole(r, v)}
                          disabled={isSelf}
                        >
                          <SelectTrigger className="w-[118px] h-8 text-[13px] border-line">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">
                              <span className="inline-flex items-center gap-1.5">
                                <Shield className="w-3.5 h-3.5" /> Admin
                              </span>
                            </SelectItem>
                            <SelectItem value="staff">
                              <span className="inline-flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" /> Staff
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="t-cell text-muted2 whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </td>
                      <td className="t-cell text-right whitespace-nowrap">
                        <button
                          onClick={() => makeResetLink(r)}
                          className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                          title="Copy a password reset link for this person"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => remove(r)}
                            className="text-muted2 hover:text-stock-out sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add someone to the team</DialogTitle>
            <DialogDescription>
              They'll be asked to choose their own password the first time they sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="tn">Name</Label>
              <Input
                id="tn"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="te">Email</Label>
              <Input
                id="te"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp">Temporary password</Label>
              <Input
                id="tp"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <p className="text-[12px] text-muted2">At least 8 characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={busy} className="bg-brand hover:bg-brand-hover">
              Add person
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
